// components/UmapDialog.jsx
import React, { useMemo, useRef, useEffect } from 'react';
import { Dialog, DialogContent, CircularProgress } from '@mui/material';
import Plot from 'react-plotly.js';

// react-plotly runs Plotly.purge on unmount, but for scattergl traces that does
// NOT release the WebGL context / GPU buffers -- memory stays allocated after
// the dialog is closed, and browsers cap the number of live contexts. Here we
// keep references to the plot's <canvas> nodes and explicitly lose their GL
// context on unmount.
function LeakSafePlot({ onInitialized, onUpdate, ...props }) {
  const canvasesRef = useRef([]);

  useEffect(() => () => {
    canvasesRef.current.forEach((c) => {
      try {
        const gl = c.getContext('webgl2') || c.getContext('webgl');
        const ext = gl && gl.getExtension('WEBGL_lose_context');
        if (ext) ext.loseContext();
      } catch (e) { /* ignore */ }
    });
    canvasesRef.current = [];
  }, []);

  const capture = (fig, gd) => {
    if (gd) canvasesRef.current = Array.from(gd.querySelectorAll('canvas'));
  };

  return (
    <Plot
      {...props}
      onInitialized={(fig, gd) => { capture(fig, gd); onInitialized && onInitialized(fig, gd); }}
      onUpdate={(fig, gd) => { capture(fig, gd); onUpdate && onUpdate(fig, gd); }}
    />
  );
}

const UmapDialog = ({
  open,
  onClose,
  graphData,
  message,
  plotComponent,        // optional: caller supplies its own <Plot> (Graphs page)
  onClickPoint,
  isFullscreen,
  windowSize,
}) => {

  // Build the plot once per actual figure/size change. Re-creating it on every
  // parent render re-plots a large WebGL figure, which leaks context/memory.
  const builtPlot = useMemo(() => {
    if (plotComponent) return null;               // caller controls the plot
    if (!graphData?.data || !windowSize) return null;
    return (
      <LeakSafePlot
        data={graphData.data}
        layout={{
          ...graphData.layout,
          width: isFullscreen ? windowSize.width * 0.96 : windowSize.width * 0.46,
          height: isFullscreen ? windowSize.height * 0.99 : windowSize.height * 0.65 - 80,
          margin: { t: 30, b: 10, l: 16, r: 10 },
        }}
        config={{ responsive: false }}
        style={{ width: '100%', overflow: 'visible' }}
        onClick={onClickPoint}
      />
    );
  }, [plotComponent, graphData, isFullscreen, windowSize?.width, windowSize?.height, onClickPoint]);

  const content = plotComponent || builtPlot;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth>
      <DialogContent style={{ padding: 0, backgroundColor: 'white', overflow: 'auto' }}>
        {content || (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', gap: 12, minHeight: 240, padding: 40 }}>
            {message ? <div>{message}</div> : <><CircularProgress /><div>UMAP wird geladen…</div></>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UmapDialog;
